/**
 * An event as schema.org describes one, for search engines, and the words the
 * page's <head> says about it. Ported from the renderer's templates/jsonLd.js
 * and lib/dates.js, with no moment: event times are Portugal wall-clock
 * strings, and the zone's offset is read through Intl.
 * https://developers.google.com/search/docs/appearance/structured-data/event
 */
import { slugify } from "@/lib/address";
import { cdnUrl } from "@/lib/cdn";
import { wallClock } from "@/lib/eventTime";
import { euros } from "@/lib/format";
import { HERO_PHOTO, SITE_DESCRIPTION } from "@/lib/site";
import type { EventDetails, EventPlace, Point } from "@/lib/types/event";
import type { EventSummary } from "@/lib/types/eventList";

export const SITE_URL = "https://montanhasdentro.pt";
export const SITE_NAME = "Montanhas Dentro";

const SCHEMA = "https://schema.org/";

const EVENT_STATUS: Partial<Record<string, string>> = {
  CANCELED: "EventCancelled",
};

const AVAILABILITY: Partial<Record<string, string>> = {
  OPEN: "InStock",
  WAITING: "PreOrder",
  CLOSED: "SoldOut",
};

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

/** the event's canonical address, with the title as it reads in one */
export const eventUrl = (event: { id: number | string; title?: string | null }) =>
  `${SITE_URL}/evento/${Number(event.id)}/${slugify(event.title)}`;

/**
 * Lisbon's offset from UTC at a wall-clock time there, as '+01:00' or '+00:00':
 * what the zone shows at the instant that reads as that time, taken as UTC
 * first, which is only wrong inside the hour the clocks change.
 */
const lisbonOffset = (y: number, m: number, d: number, hh: number, mm: number, ss: number) => {
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm, ss));
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Lisbon", timeZoneName: "longOffset" }).formatToParts(guess);
  const name = parts.find((part) => part.type === "timeZoneName")?.value || "GMT";
  const match = /GMT([+-]\d{2}:\d{2})?/.exec(name);
  return match && match[1] ? match[1] : "+00:00";
};

/** '2026-09-19T15:30:00+01:00', for JSON-LD; +00:00 in winter rather than Z */
export const isoTime = (time: string | null | undefined) => {
  const text = wallClock(time);
  if (!text) return null;
  const [date, clock = "00:00:00"] = text.split(" ");
  const [y, m, d] = date.split("-").map(Number);
  const [hh = 0, mm = 0, ss = 0] = clock.split(":").map(Number);
  if (!y || !m || !d) return null;
  return `${date}T${clock}${lisbonOffset(y, m, d, hh, mm, ss)}`;
};

/**
 * When an event is, in words: "3 de outubro de 2026", or "3 a 5 de outubro de
 * 2026" for one over several days. The same wording as the share preview's and
 * the renderer's (ShareService.dateText in the public API).
 */
export const eventDateWords = (start_time: string | null | undefined, end_time?: string | null) => {
  const parse = (value: string | null | undefined) => {
    const text = wallClock(value);
    if (!text) return null;
    const [y, m, d] = text.slice(0, 10).split("-").map(Number);
    return y && m && d ? { y, m, d } : null;
  };
  const start = parse(start_time);
  if (!start) return "";
  const end = parse(end_time) || start;
  const day = (x: { m: number; d: number }) => `${x.d} de ${MONTHS[x.m - 1]}`;
  const after = end.y > start.y || (end.y === start.y && (end.m > start.m || (end.m === start.m && end.d > start.d)));

  if (!after) {
    return `${day(start)} de ${start.y}`;
  }
  if (start.y !== end.y) {
    return `${day(start)} de ${start.y} a ${day(end)} de ${end.y}`;
  }
  if (start.m !== end.m) {
    return `${day(start)} a ${day(end)} de ${end.y}`;
  }
  return `${start.d} a ${day(end)} de ${end.y}`;
};

/**
 * A text with its letters as letters. Some descriptions were written with the
 * "bold" and "italic" letters of Unicode's mathematical block (𝐏𝐨ç𝐨𝐬), which
 * look like styled text and are not letters to a search engine or a screen
 * reader: each is put back to the plain one it stands for. Nothing else changes.
 */
export const plainLetters = (text: string | null | undefined) =>
  String(text || "").replace(/[\u{1D400}-\u{1D7FF}]/gu, (letter) => letter.normalize("NFKC"));

/** the start of a text, on one line, for a meta description */
export const summary = (text: string | null | undefined, max = 160) => {
  const line = plainLetters(text)
    .replace(/\s+/g, " ")
    .trim();
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
};

/** a link typed in by hand, only if it is one a browser should follow */
export const safeLink = (url: string | null | undefined) => {
  const text = String(url || "").trim();
  return /^https?:\/\//i.test(text) ? text : null;
};

/** an event's coordinates, or null: some rows hold POINT(0 0) for a location never set */
export const coordinates = (point: Point | { x: number | string; y: number | string } | null | undefined) =>
  point && (Number(point.x) || Number(point.y)) ? { latitude: Number(point.y), longitude: Number(point.x) } : null;

/** the banner first, as the app shows it: the event's own, or else its programme's; a wide one, where there is one */
export const eventBanner = (event: Pick<EventDetails, "event_banners" | "banners">) =>
  (event.event_banners || event.banners || [])[0] || null;

/** the square banner made for a shared link's preview, the event's own or else its programme's, or null */
export const squareBanner = (event: Pick<EventDetails, "event_banners" | "banners">) =>
  (event.event_banners || event.banners || []).find((banner) => banner.banner_format === "SQUARE") || null;

// the sizes the public API draws the preview image at (ShareService): a square banner whole, a wide one cut
const PREVIEW_WIDE = { width: 1200, height: 630 };
const PREVIEW_SQUARE = { width: 1080, height: 1080 };

/**
 * The image a link to the event shows, for the page's metadata: the square banner where
 * there is one, which WhatsApp shows whole, and the wide one for X, which draws every
 * preview wide (?larga). The banner's id rides on each address, so a new banner is a new
 * image to the caches in between.
 */
export const eventPreviewImages = (event: Pick<EventDetails, "id" | "event_banners" | "banners">, alt: string) => {
  const address = `${SITE_URL}/evento/${event.id}/imagem.jpg`;
  const wide = eventBanner(event);
  const square = squareBanner(event);
  const wideImage = wide ? `${address}?larga&v=${wide.id}` : address;
  return {
    openGraph: [square ? { url: `${address}?v=${square.id}`, ...PREVIEW_SQUARE, alt } : { url: wideImage, ...PREVIEW_WIDE, alt }],
    twitter: [wideImage],
  };
};

// how many photos the structured data lists
const MAX_PHOTOS = 12;

/** what the JSON-LD gives as the event's images, full size */
export const eventImages = (event: Pick<EventDetails, "event_banners" | "banners" | "photos">) => {
  const banner = eventBanner(event);
  return [...(banner ? [cdnUrl(banner.path)] : []), ...(event.photos || []).slice(0, MAX_PHOTOS).map((photo) => cdnUrl(photo.path))];
};

/**
 * The prices shown to someone who is not signed in: Adulto, and Criança when
 * the event has a child price. The API sends the prices in force today, and
 * while an early price is still valid, the later one each goes up to.
 */
export const eventPrices = (event: Pick<EventDetails, "price" | "late_price" | "price_child" | "late_price_child">) => {
  if (event.price == null) {
    return [];
  }
  const prices = [{ name: "Adulto", price: Number(event.price), latePrice: event.late_price }];
  if (Number(event.price_child) > 0) {
    prices.push({ name: "Criança", price: Number(event.price_child), latePrice: event.late_price_child });
  }
  return prices;
};

/** the park or serra an event is in, each name once */
const areaNames = (place: EventPlace | null | undefined) => [...new Set((place?.areas || []).map((area) => area.name))];

/**
 * Where an event is, on one line: "Poço do Inferno · Manteigas, Guarda · Serra da
 * Estrela". The first trail's trailhead, the concelho and its distrito, then the park
 * or serra; a distrito named like its concelho is said once. Empty when nothing is known.
 */
export const placeLine = (place: EventPlace | null | undefined) => {
  if (!place) return "";
  const town = [...new Set([place.locality, place.region].filter(Boolean))].join(", ");
  // the trailhead first: where the walk starts, by the name the place goes by
  return [place.trailhead, town, ...areaNames(place).slice(0, 2)].filter(Boolean).join(" · ");
};

/** the same in a sentence: "em Manteigas (Serra da Estrela)" */
const placeWords = (place: EventPlace | null | undefined) => {
  const area = areaNames(place)[0];
  const town = place?.locality || place?.region;
  if (!town) return area ? `em ${area}` : "";
  return `em ${town}${area ? ` (${area})` : ""}`;
};

const place = (event: EventDetails) => {
  // Google asks for a place with a name and an address; the name is the park or
  // the concelho, and only an event we know nothing of is named by its title
  const known = event.place;
  const location: Record<string, unknown> = { "@type": "Place", name: areaNames(known)[0] || known?.locality || String(event.title || "").trim() };
  if (known) {
    location.address = {
      "@type": "PostalAddress",
      addressLocality: known.locality || undefined,
      addressRegion: known.region || undefined,
      addressCountry: known.country,
    };
  }
  const point = coordinates(event.location);
  if (point) {
    location.geo = { "@type": "GeoCoordinates", ...point };
  }
  const map = safeLink(event.google_maps_link);
  if (map) {
    location.hasMap = map;
  }
  return location;
};

/**
 * The event's structured data, as the text of a <script type="application/ld+json">:
 * nothing in it may close the tag, so every < is escaped.
 */
export const eventJsonLd = (event: EventDetails, images: string[]) => {
  const url = eventUrl(event);
  // a full event is sold out whatever its registration says, and one with its
  // last places left is limited; see public-api's lib/availability.js
  const availability =
    event.availability === "SOLD_OUT" ? "SoldOut" : event.availability === "LAST_PLACES" ? "LimitedAvailability" : AVAILABILITY[event.registration_state];

  const data = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: String(event.title || "").trim(),
    url,
    startDate: isoTime(event.start_time),
    endDate: isoTime(event.end_time) || undefined,
    eventStatus: SCHEMA + (EVENT_STATUS[event.state] || "EventScheduled"),
    eventAttendanceMode: `${SCHEMA}OfflineEventAttendanceMode`,
    location: place(event),
    description: plainLetters(event.short_description).trim() || eventDescription(event),
    image: images.length > 0 ? images : [cdnUrl("logo-new.png")],
    organizer: ORGANIZER,
    offers: eventPrices(event).map(({ name, price }) => ({
      "@type": "Offer",
      name,
      price,
      priceCurrency: "EUR",
      url,
      availability: availability ? SCHEMA + availability : undefined,
      validFrom: event.on_sale_from || undefined,
    })),
  };
  return JSON.stringify(data).replace(/</g, "\\u003c");
};

/** the text of a <script type="application/ld+json">: nothing in it may close the tag */
export const jsonLdText = (data: unknown) => JSON.stringify(data).replace(/</g, "\\u003c");

const ORGANIZATION_ID = `${SITE_URL}/#organization`;

/**
 * Who runs an event: the business the home page describes, named by the same
 * @id, so every event is read as that one company's.
 */
const ORGANIZER = { "@type": "Organization", "@id": ORGANIZATION_ID, name: SITE_NAME, url: SITE_URL };

/**
 * Our listing on Google Maps (the Business Profile), by its lasting id. The
 * profile shows no address, since nobody is received there, so neither does
 * the data below: the locality and the area we serve.
 */
export const GOOGLE_PROFILE = "https://www.google.com/maps?cid=15524586134817538840";

/**
 * The company as a local business, for the home page: who we are, where we are
 * and the area we serve, which is what a search for walks in a region matches on;
 * and the site by its name, which is what a result shows over its address.
 */
export const siteJsonLd = (sameAs: string[]) =>
  jsonLdText({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": ORGANIZATION_ID,
        name: SITE_NAME,
        legalName: "Garça Clandestina, Lda",
        url: SITE_URL,
        logo: cdnUrl("logo-new.png"),
        image: cdnUrl(HERO_PHOTO),
        description: SITE_DESCRIPTION,
        telephone: "+351937267541",
        email: "info@montanhasdentro.pt",
        address: { "@type": "PostalAddress", addressLocality: "Perafita, Matosinhos", addressRegion: "Porto", addressCountry: "PT" },
        areaServed: { "@type": "Place", name: "Norte de Portugal" },
        sameAs: [...sameAs, GOOGLE_PROFILE],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: SITE_URL,
        inLanguage: "pt-PT",
        publisher: { "@id": ORGANIZATION_ID },
      },
    ],
  });

/**
 * Where an event's page is in the site, which a search result shows in place of
 * the address: Início, Caminhadas, the event.
 */
export const eventBreadcrumbJsonLd = (event: Pick<EventDetails, "id" | "title">) =>
  jsonLdText({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { name: "Início", item: `${SITE_URL}/` },
      { name: "Caminhadas", item: `${SITE_URL}/explorar` },
      { name: String(event.title || "").trim(), item: eventUrl(event) },
    ].map((crumb, index) => ({ "@type": "ListItem", position: index + 1, ...crumb })),
  });

const LEVEL_WORDS: Record<string, string> = { EASY: "fácil", MODERATE: "moderada", HARD: "difícil" };

/**
 * What a search result says of an event when staff wrote no short description:
 * the date, what it is and how hard, from what age, the price, and who we are --
 * rather than the date alone, which told nobody anything.
 */
export function eventDescription(event: Pick<EventDetails, "start_time" | "end_time" | "short_description" | "type" | "difficulty" | "min_age" | "price" | "place">) {
  const written = summary(event.short_description);
  const when = eventDateWords(event.start_time, event.end_time);
  if (written) {
    return [when, written].filter(Boolean).join(" · ");
  }
  const level = LEVEL_WORDS[String(event.difficulty || "").split("_")[0]] || "";
  const where = placeWords(event.place);
  const kind = event.type && event.type !== "HIKE" ? where.replace(/^em /, "Em ") : `Caminhada${level ? ` ${level}` : ""} em grupo${where ? ` ${where}` : ""}`;
  const age = Number(event.min_age) > 0 ? `a partir dos ${event.min_age} anos` : "";
  const price = Number(event.price) > 0 ? `${euros(event.price)} por adulto` : "";
  return [when, [kind, age].filter(Boolean).join(", "), price, "Inscreve-te na Montanhas Dentro, caminhadas no Norte de Portugal"].filter(Boolean).join(" · ");
}

/**
 * A list of events as an ItemList of Event, for Explore: each with what the
 * list knows -- the name, when, where it starts, its banner and the adult
 * price -- the way the event's own page says it in full.
 */
export const eventListJsonLd = (events: EventSummary[], url: string) =>
  jsonLdText({
    "@context": "https://schema.org",
    "@type": "ItemList",
    url,
    numberOfItems: events.length,
    itemListElement: events.map((event, index) => {
      const eventUrlOf = eventUrl(event);
      const banner = (event.banners || [])[0];
      const geo = coordinates(event.location);
      return {
        "@type": "ListItem",
        position: index + 1,
        url: eventUrlOf,
        item: {
          "@type": "Event",
          name: String(event.title || "").trim(),
          url: eventUrlOf,
          startDate: isoTime(event.start_time),
          endDate: isoTime(event.end_time) || undefined,
          eventAttendanceMode: `${SCHEMA}OfflineEventAttendanceMode`,
          eventStatus: `${SCHEMA}${event.state === "CANCELED" ? "EventCancelled" : "EventScheduled"}`,
          location: { "@type": "Place", name: String(event.title || "").trim(), ...(geo ? { geo: { "@type": "GeoCoordinates", ...geo } } : {}) },
          image: banner ? [cdnUrl(banner.path)] : undefined,
          organizer: ORGANIZER,
          offers:
            event.price != null
              ? [{ "@type": "Offer", name: "Adulto", price: Number(event.price), priceCurrency: "EUR", url: eventUrlOf, availability: `${SCHEMA}${event.availability === "SOLD_OUT" ? "SoldOut" : "InStock"}` }]
              : undefined,
        },
      };
    }),
  });
